/**
 * Data layer for `clickdealer-integration-via-api`. Thin typed fetch wrappers over /api/v1/clickdealer-integration-via-api.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ClickdealerCredentialsRow {
	id: string;
	cd_account_id: string;
	cd_api_key_preview: string;
	status: string;
	created_at: number;
}

export interface ClickdealerOfferRow {
	id: string;
	tenant_id: string;
	cd_offer_id: string;
	trackerity_offer_id: string;
	created_at: number;
}

export interface ClickdealerPostbackRow {
	id: string;
	tenant_id: string;
	cd_click_id: string;
	cd_offer_id: string;
	action_id: string;
	payout_cents: number;
	status: string;
	raw_payload: string;
	created_at: number;
}

export interface ClickdealerStatusResponse {
	feature: string;
	has_credentials: boolean;
	has_offer_mappings: boolean;
	has_postbacks: boolean;
	items: unknown[];
}

export interface ListCredentialsResponse {
	items: ClickdealerCredentialsRow[];
}

export interface ListOffersResponse {
	items: ClickdealerOfferRow[];
}

export interface ListPostbacksResponse {
	items: ClickdealerPostbackRow[];
}

export interface CreateCredentialsBody {
	cd_account_id: string;
	cd_api_key: string;
}

export interface CreateOfferBody {
	cd_offer_id: string;
	trackerity_offer_id: string;
}

const BASE = "/api/v1/clickdealer-integration-via-api";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getStatus(): Promise<ClickdealerStatusResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ClickdealerStatusResponse>(res);
}

export async function listCredentials(): Promise<ListCredentialsResponse> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCredentialsResponse>(res);
}

export async function createCredentials(
	body: CreateCredentialsBody,
): Promise<{ ok: boolean; item: ClickdealerCredentialsRow }> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; item: ClickdealerCredentialsRow }>(res);
}

export async function listOffers(): Promise<ListOffersResponse> {
	const res = await fetch(`${BASE}/offers`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListOffersResponse>(res);
}

export async function createOffer(
	body: CreateOfferBody,
): Promise<{ ok: boolean; item: ClickdealerOfferRow }> {
	const res = await fetch(`${BASE}/offers`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; item: ClickdealerOfferRow }>(res);
}

export async function listPostbacks(): Promise<ListPostbacksResponse> {
	const res = await fetch(`${BASE}/postbacks`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostbacksResponse>(res);
}

export const clickdealerIntegrationViaApiQueryKeys = {
	all: ["clickdealer-integration-via-api"] as const,
	status: () => [...clickdealerIntegrationViaApiQueryKeys.all, "status"] as const,
	credentials: () => [...clickdealerIntegrationViaApiQueryKeys.all, "credentials"] as const,
	offers: () => [...clickdealerIntegrationViaApiQueryKeys.all, "offers"] as const,
	postbacks: () => [...clickdealerIntegrationViaApiQueryKeys.all, "postbacks"] as const,
};
